import { gql } from "@apollo/client";
import { hasModule } from "csi/_base/modules";
import PostRequest from "csi/comm/PostRequest";
import getApolloClient from "csi/graphql/ApolloClient";
import csi from "csi/main";
import message from "csi/message";
import { submitWriter } from "csi/service/writerService";
import sessionConstants from "csi/sessionConstants";
import { sessionStore } from "csi/state/rootStore";
import { C8Node, cnl } from "csi/typings";
import xml from "csi/xml";
import Writer from "csi/xml/Writer";
import { throttle } from "lodash";
import camunda from "src/Camunda/main";
import { proofingUpdateAssignedTaskCount } from "src/Proofing/main";
import { Subscription } from "zen-observable-ts";

export interface NotificationNode {
	contextCNL: cnl;
	contextType: string;
	createdAt: number; // time
	id: number;
	readAt: number; // time
	userCNL: cnl;
	message: string;
	title: string;
	initiatorCNL: cnl;
	targetLocation: string;
	status: NotificationStatus;
}

export interface GetNotificationsResponse {
	notificationNodes: NotificationNode[];
	resultNodes: C8Node[];
	supportNodes: C8Node[];
}

interface ContextTypeKeyValue {
	key: string;
	value: number;
}

interface TypeKeyValue {
	key: "CommentReply" | "UserTask" | "Comment" | "System";
	value: number;
}

interface NotificationCountResult {
	countsByContextType: ContextTypeKeyValue[];
	countsByType: TypeKeyValue[];
	totalCount: number;
}

export const NOTIFICATION_TYPE_SYSTEM = "System";
const NOTIFICATION_TYPE_COMMENT = "Comment";
const NOTIFICATION_TYPE_COMMENT_REPLY = "CommentReply";
const NOTIFICATION_TYPE_CAMUNDA = "CamundaTask";
const NOTIFICATION_TYPE_PROOFING = "ProofTask";
export const LAST_CHECKED_NOTIFICATION_ATTR_ID = "LastCheckedNotificationTime";


export const NOTIFICATION_STATUS_SUCCESSFUL = "SUCCESSFUL";
export const NOTIFICATION_STATUS_ERROR = "ERROR";

export type NotificationStatus = typeof NOTIFICATION_STATUS_SUCCESSFUL | typeof NOTIFICATION_STATUS_ERROR;

let subscriptionHandler = null;
const lazyUpdateNotificationCounts = throttle(updateNotificationsAndTasksCount, 2 * 1000);

/** get list of notifications with pagination for current user */
export async function getNotifications(unread: boolean, nodePaths: string[], start: number, count: number): Promise<GetNotificationsResponse> {
	const request = new PostRequest("SiteAdmin", "GetNotifications");
	request.addParameter("Unread", unread);
	request.addParameter("NodePaths", nodePaths);
	request.addParameter("Start", start);
	request.addParameter("Count", count);
	try {
		const response = await request.go();
		const { ResultNode: resultNodes, Node: supportNodes } = response.data.NODES;
		const values = response.getValues();
		const notificationNodes = JSON.parse(values[0].Value);
		return { notificationNodes, resultNodes, supportNodes };
	} catch (error) {
		message.errorDialog({ message: error.message });
		return null;
	}
}

/** change notifications read/unread status */
export async function updateNotifications(ids: number[], read: boolean): Promise<void> {
	const writer = new Writer();
	writer
		.callMethod("SiteAdmin", "UpdateNotifications")
		.addParameter("Ids", xml.ATTR_INT64LIST, ids)
		.addParameter("Read", xml.ATTR_BOOLEAN, read);
	try {
		const response = await submitWriter(writer);
	} catch (error) {
		message.error(error.message);
	}
}

/** get the new notifications count for the current user */
export async function getNewNotificationsCounts(): Promise<NotificationCountResult | null> {
	const lastCheckedTime = sessionStore.userDetails[LAST_CHECKED_NOTIFICATION_ATTR_ID];

	const request = new PostRequest("SiteAdmin", "GetNotificationsCounts")
		.addParameter("CreatedOnOrAfter", lastCheckedTime);

	try {
		const response = await request.go();
		return response.data;
	} catch (error) {
		message.error(error.message);
		return null;
	}
}

/** filter given notifications and return only comments and comment replies ones */
export function getCommentsWithMentionsUrls(notifications: NotificationNode[]): NotificationNode[] {
	return notifications.filter((notification) => {
		const type = notification.contextType;
		return (type === NOTIFICATION_TYPE_COMMENT) || (type === NOTIFICATION_TYPE_COMMENT_REPLY);
	});
}

/** filter given notifications and return only messages */
export function getMessagesForCurrentUser(notifications: NotificationNode[]): NotificationNode[] {
	return notifications.filter(({ contextType, userCNL }) => {
		return (contextType === NOTIFICATION_TYPE_SYSTEM) && (userCNL === sessionConstants.userUrl);
	});
}

/** filter given notifications and return only Camunda and Proofing ones */
export function getCamundaAndProofingTasks(notifications: NotificationNode[]): NotificationNode[] {
	return notifications.filter((notification) => {
		const type = notification.contextType;
		return (type === NOTIFICATION_TYPE_CAMUNDA) || (type === NOTIFICATION_TYPE_PROOFING);
	});
}

/** return true if given notification is new (unread) */
export function isUnreadNotification(notification: NotificationNode): boolean {
	return notification.readAt === 0;
}

/** Subscribe to notifications about Proof, Camunda and Mention */
export function subscribeToNotificationCount(
	newDataCallback: ((notificationCountResult: NotificationCountResult) => void),
	checkTime: number,
): Subscription {
	return getApolloClient().subscribe({
		query: gql(`
			subscription($checkTime: Long) {
				notificationCountResult(
					filter: {
						createdOnOrAfter: $checkTime
					}
				){
					countsByContextType{
						key
						value
					}
					countsByType{
						key
						value
					}
					totalCount
				}
			}
		`),
		variables: { checkTime },
	})
		.subscribe({
			next(data) {
				// Notify your application with the new arrived data
				newDataCallback(data.data.notificationCountResult);
			},
		});
}

/** Retrieve initial number of notifications from server and subscribe for further notifications  */
export function initNotificationService(): void {
	const lastCheckedTime = sessionStore.userDetails[LAST_CHECKED_NOTIFICATION_ATTR_ID];
	subscriptionHandler = subscribeToNotificationCount(lazyUpdateNotificationCounts, lastCheckedTime);

	getNewNotificationsCounts().then((data) => {
		data && updateNotificationsAndTasksCount(data);
	});
}

/** Callback for notifications subscription */
function updateNotificationsAndTasksCount(notificationCountResult: NotificationCountResult): void {
	const { hasNewConversationNotifications, hasNewTaskNotifications, hasNewMessageNotifications, notificationDropdownIsOpen } = sessionStore;

	// only update the count when it's closed
	if (!notificationDropdownIsOpen) {
		sessionStore.setNotificationCount(notificationCountResult.totalCount);
	}

	// handle the new notification for tabs
	let hasNewConversations = hasNewConversationNotifications;
	let hasNewTasks = hasNewTaskNotifications;
	let hasNewMessages = hasNewMessageNotifications;

	notificationCountResult.countsByType.forEach(({ key }) => {
		if (key === NOTIFICATION_TYPE_COMMENT || key === NOTIFICATION_TYPE_COMMENT_REPLY) {
			hasNewConversations = true;
		} else if (key === "UserTask") {
			hasNewTasks = true;
		} else if (key === NOTIFICATION_TYPE_SYSTEM) {
			hasNewMessages = true;
		}
	});

	sessionStore.setHasNewConversationNotifications(hasNewConversations);
	sessionStore.setHasNewTaskNotifications(hasNewTasks);
	sessionStore.setHasNewMessageNotifications(hasNewMessages);

	if (hasModule("Camunda")) {
		camunda.updateAssignedTaskCount();
	}

	if (hasModule("Proofing")) {
		proofingUpdateAssignedTaskCount();
	}
}

/** clear the notification count and re-subscription based on the current time */
export function clearNotification(): void {
	sessionStore.setNotificationCount(0);

	// cancel the previous subscription
	subscriptionHandler?.unsubscribe();
	const serverTime = csi.getCurrentTime();

	subscriptionHandler = subscribeToNotificationCount(lazyUpdateNotificationCounts, serverTime);
}